/**
 * BacktrayInlineCreate (Iter229w · extracted Iter230a)
 * ────────────────────────────────────────────────────
 * Mirror of PenetrationInlineCreate but in the opposite direction:
 * when the operator clicks "+ Create new Backtray" inside the
 * Pin.Penetration dialog, this inline form lets them pick the
 * Backtray's flashing template + length without closing the dialog.
 *
 * `onSave({linked_flashing_template_id, linked_flashing_name, length_m})`
 * returns just the cascade fields; parent assigns id / x / y / type.
 *
 * Was a named export inside PenetrationEditPanel.js until Iter230a —
 * extracted to its own file as part of the tech-debt split.
 * Behaviour is byte-identical; only the import path changed.
 */
import React, { useState } from 'react';
import { normProfile } from './cascadeHelpers';

export function BacktrayInlineCreate({
  allTemplates = [],
  roofProfile,
  matchedTemplateId,
  matchedTemplateName,
  onSave,
  onCancel,
}) {
  // Filter to Back Tray category templates. Prefer those whose
  // sheet_profile / sheet_profiles matches the current roof profile,
  // but always show all Back Tray templates as a fallback under a
  // collapsed "All" toggle so admins can pick across profiles.
  const wantProfile = normProfile(roofProfile);
  const backtrayTpls = (allTemplates || []).filter(
    t => String(t.category || '').toLowerCase() === 'back tray'
  );
  const profileMatched = backtrayTpls.filter(t => {
    // C11 (2026-02c) — `applies_to_all_profiles` wins over the
    // profile arrays. If the flag is set the template is ALWAYS a
    // profile-match regardless of what sheet_profile/s hold (post-
    // backfill they should be empty anyway · this guards the read
    // path so pre-backfill data + future admin-console mistakes
    // still resolve to the intended "any-profile" behaviour).
    if (t.applies_to_all_profiles) return true;
    const sps = Array.isArray(t.sheet_profiles) ? t.sheet_profiles : [];
    const legacy = t.sheet_profile || '';
    return [...sps, legacy].some(sp => sp && normProfile(sp) === wantProfile);
  });
  const others = backtrayTpls.filter(t => !profileMatched.includes(t));

  const [showAll, setShowAll] = useState(false);
  const [tplId, setTplId] = useState(matchedTemplateId || '');
  const [length, setLength] = useState('');

  const selectedTpl = backtrayTpls.find(t => t._id === tplId) || null;
  const ready = !!tplId;

  const handleSave = () => {
    if (!ready) return;
    onSave({
      linked_flashing_template_id: tplId,
      linked_flashing_name: selectedTpl?.name || matchedTemplateName || '',
      length_m: length ? parseFloat(length) : 0,
    });
  };

  const visibleTpls = showAll ? backtrayTpls : (profileMatched.length > 0 ? profileMatched : backtrayTpls);

  return (
    <div
      data-testid="backtray-inline-create"
      className="mt-2 p-2 bg-sky-50 ring-1 ring-sky-300 rounded space-y-2"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider font-bold text-sky-800">
          New Backtray — configure &amp; link
        </div>
        <button
          type="button"
          data-testid="backtray-inline-cancel"
          onClick={onCancel}
          className="text-[10px] text-slate-500 hover:text-slate-700 underline"
        >Cancel</button>
      </div>

      {/* Profile chip + toggle */}
      <div className="flex items-center justify-between">
        <span
          className="text-[9px] uppercase font-semibold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded px-1.5 py-0.5"
          title="Templates are filtered to match this roof profile"
        >Profile · {roofProfile || '—'}</span>
        {others.length > 0 && (
          <button
            type="button"
            data-testid="backtray-inline-show-all"
            onClick={() => setShowAll(v => !v)}
            className="text-[10px] text-slate-500 hover:text-slate-700 underline"
          >{showAll ? 'Show profile-matched only' : `Show all (${backtrayTpls.length})`}</button>
        )}
      </div>

      {/* Template select */}
      <div>
        <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">
          ❶ Flashing template
        </label>
        <select
          data-testid="backtray-inline-template"
          value={tplId}
          onChange={e => setTplId(e.target.value)}
          className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs bg-white"
        >
          <option value="">— Pick a Back Tray template —</option>
          {visibleTpls.map(t => {
            const sps = Array.isArray(t.sheet_profiles) && t.sheet_profiles.length > 0
              ? t.sheet_profiles.join(', ')
              : (t.sheet_profile || '—');
            return (
              <option key={t._id} value={t._id}>{t.name} · {sps}</option>
            );
          })}
        </select>
        {matchedTemplateName && !tplId && (
          <div className="mt-1 text-[10px] text-sky-700">
            Suggested from cascade: <strong>{matchedTemplateName}</strong>
            <button
              type="button"
              data-testid="backtray-inline-use-match"
              onClick={() => setTplId(matchedTemplateId || '')}
              className="ml-1 underline"
            >use</button>
          </div>
        )}
        {visibleTpls.length === 0 && (
          <div className="mt-1 text-[10px] text-amber-700 italic">
            No Back Tray templates match profile <strong>{roofProfile || '—'}</strong>. Click &ldquo;Show all&rdquo; or seed a template.
          </div>
        )}
      </div>

      {/* Length */}
      <div>
        <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">
          ❷ Length (m) <span className="text-slate-400 font-normal normal-case">— optional, can be measured later</span>
        </label>
        <input
          type="number"
          step="0.01"
          min={0}
          data-testid="backtray-inline-length"
          value={length}
          onChange={e => setLength(e.target.value)}
          placeholder="e.g. 0.6"
          className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs font-mono-num"
        />
      </div>

      {/* Selected template confirmation card */}
      {selectedTpl && (
        <div
          data-testid="backtray-inline-tpl-card"
          className="bg-emerald-50 ring-1 ring-emerald-200 rounded px-2 py-1.5 text-[11px]"
        >
          <div className="font-semibold text-emerald-900">✓ {selectedTpl.name}</div>
          <div className="text-[10px] text-emerald-700">
            {(selectedTpl.folds || []).length} fold{(selectedTpl.folds || []).length === 1 ? '' : 's'}
            {selectedTpl.material ? ` · ${selectedTpl.material}` : ''}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          data-testid="backtray-inline-save"
          onClick={handleSave}
          disabled={!ready}
          className={`px-3 py-1.5 text-[11px] font-bold rounded ${
            ready
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >Save Backtray &amp; Link</button>
      </div>
    </div>
  );
}
